import { Fragment, useState } from "react";
import { ChannelEnabledToggle } from "../../atoms/ChannelEnabledToggle";
import { SecretInput } from "../../atoms/SecretInput";
import { TestSendPanel } from "../../molecules/TestSendPanel";
import type {
  ChannelSettingResponse,
  WebhookAuthType,
  WebhookConfig,
  WebhookHttpMethod,
} from "../../../types/subscriptionSettings";
import type { UseChannelSettings } from "../../../hooks/useChannelSettings";

interface Props {
  ctrl: UseChannelSettings;
  setting: ChannelSettingResponse;
}

interface HeaderRow {
  key: string;
  value: string;
}

function headersToRows(h: Record<string, string> | undefined): HeaderRow[] {
  if (!h) return [{ key: "", value: "" }];
  const entries = Object.entries(h);
  return entries.length ? entries.map(([key, value]) => ({ key, value })) : [{ key: "", value: "" }];
}

function rowsToHeaders(rows: HeaderRow[]): Record<string, string> {
  return rows.reduce<Record<string, string>>((acc, r) => {
    if (r.key.trim()) acc[r.key.trim()] = r.value;
    return acc;
  }, {});
}

export function WebhookChannelTab({ ctrl, setting }: Props) {
  const initial = setting.config as WebhookConfig;
  const [enabled, setEnabled] = useState(setting.is_enabled);
  const [endpointUrl, setEndpointUrl] = useState(initial.endpoint_url ?? "");
  const [httpMethod, setHttpMethod] = useState<WebhookHttpMethod>(initial.http_method ?? "POST");
  const [headerRows, setHeaderRows] = useState<HeaderRow[]>(headersToRows(initial.headers));
  const [authType, setAuthType] = useState<WebhookAuthType>(initial.auth_type ?? "none");
  const [secret, setSecret] = useState("");
  const [maxRetries, setMaxRetries] = useState(String(initial.max_retries ?? 3));
  const [backoffSeconds, setBackoffSeconds] = useState(String(initial.backoff_seconds ?? 30));
  const [saveStatus, setSaveStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");

  const errors = ctrl.fieldErrors.webhook;
  const secretIsSet = initial.secret?.is_set ?? false;

  async function handleSave() {
    setSaveStatus("saving");
    const config: Record<string, unknown> = {
      endpoint_url: endpointUrl,
      http_method: httpMethod,
      headers: rowsToHeaders(headerRows),
      auth_type: authType,
      max_retries: Number(maxRetries),
      backoff_seconds: Number(backoffSeconds),
    };
    if (secret) config.secret = secret;
    const result = await ctrl.save("webhook", { is_enabled: enabled, config });
    setSaveStatus(result ? "saved" : "error");
    if (result) setSecret("");
  }

  function updateHeader(idx: number, field: "key" | "value", value: string) {
    setHeaderRows((rows) => rows.map((r, i) => (i === idx ? { ...r, [field]: value } : r)));
  }

  function addHeader() {
    setHeaderRows((rows) => [...rows, { key: "", value: "" }]);
  }

  function removeHeader(idx: number) {
    setHeaderRows((rows) => (rows.length === 1 ? [{ key: "", value: "" }] : rows.filter((_, i) => i !== idx)));
  }

  return (
    <div className="ac-channel-tab">
      <div className="ac-channel-tab__header">
        <h2 className="ac-channel-tab__title">Webhook</h2>
        <ChannelEnabledToggle enabled={enabled} onChange={setEnabled} />
      </div>

      <div className="ac-form-row">
        <label className="ac-form-row__label" htmlFor="webhook-url">Endpoint URL</label>
        <input id="webhook-url" className="ac-input" value={endpointUrl} onChange={(e) => setEndpointUrl(e.target.value)} placeholder="https://example.com/hook" />
        {errors.endpoint_url && <span className="ac-form-row__error">{errors.endpoint_url.join(" ")}</span>}
      </div>

      <div className="ac-form-row">
        <label className="ac-form-row__label" htmlFor="webhook-method">HTTP method</label>
        <select id="webhook-method" className="ac-select" value={httpMethod} onChange={(e) => setHttpMethod(e.target.value as WebhookHttpMethod)}>
          <option value="POST">POST</option>
          <option value="PUT">PUT</option>
          <option value="PATCH">PATCH</option>
        </select>
        {errors.http_method && <span className="ac-form-row__error">{errors.http_method.join(" ")}</span>}
      </div>

      <div className="ac-form-row">
        <span className="ac-form-row__label">Headers</span>
        <div className="ac-headers-grid">
          {headerRows.map((row, idx) => (
            <Fragment key={idx}>
              <input className="ac-input" value={row.key} onChange={(e) => updateHeader(idx, "key", e.target.value)} placeholder="Header name" />
              <input className="ac-input" value={row.value} onChange={(e) => updateHeader(idx, "value", e.target.value)} placeholder="Value" />
              <button type="button" className="ac-headers-grid__remove" onClick={() => removeHeader(idx)}>Remove</button>
            </Fragment>
          ))}
        </div>
        <button type="button" className="ac-button ac-button--secondary" onClick={addHeader} style={{ marginTop: 8 }}>+ Add header</button>
        {errors.headers && <span className="ac-form-row__error">{errors.headers.join(" ")}</span>}
      </div>

      <div className="ac-form-row">
        <label className="ac-form-row__label" htmlFor="webhook-auth">Auth type</label>
        <select id="webhook-auth" className="ac-select" value={authType} onChange={(e) => setAuthType(e.target.value as WebhookAuthType)}>
          <option value="none">None</option>
          <option value="bearer">Bearer</option>
          <option value="basic">Basic (user:password)</option>
        </select>
      </div>

      {authType !== "none" && (
        <div className="ac-form-row">
          <label className="ac-form-row__label" htmlFor="webhook-secret">Secret</label>
          <SecretInput id="webhook-secret" value={secret} onChange={setSecret} isSet={secretIsSet} ariaLabel="Webhook secret" />
          {errors.secret && <span className="ac-form-row__error">{errors.secret.join(" ")}</span>}
        </div>
      )}

      <div className="ac-form-row">
        <label className="ac-form-row__label" htmlFor="webhook-retries">Max retries (0-10)</label>
        <input id="webhook-retries" className="ac-input" value={maxRetries} onChange={(e) => setMaxRetries(e.target.value)} />
        {errors.max_retries && <span className="ac-form-row__error">{errors.max_retries.join(" ")}</span>}
      </div>

      <div className="ac-form-row">
        <label className="ac-form-row__label" htmlFor="webhook-backoff">Backoff seconds (1-300)</label>
        <input id="webhook-backoff" className="ac-input" value={backoffSeconds} onChange={(e) => setBackoffSeconds(e.target.value)} />
        {errors.backoff_seconds && <span className="ac-form-row__error">{errors.backoff_seconds.join(" ")}</span>}
      </div>

      <div className="ac-button-row">
        <button type="button" className="ac-button" onClick={handleSave} disabled={saveStatus === "saving"}>
          {saveStatus === "saving" ? "Saving..." : "Save"}
        </button>
        {saveStatus === "saved" && <span className="ac-alert ac-alert--success" role="status">Saved.</span>}
        {saveStatus === "error" && <span className="ac-alert ac-alert--error" role="status">Failed to save.</span>}
      </div>

      <TestSendPanel
        destinationLabel="Override endpoint URL (optional)"
        placeholder="(use saved URL)"
        disabled={!enabled}
        onSend={(dest) => ctrl.test("webhook", dest)}
      />
    </div>
  );
}
